import { create } from "zustand";
import { RequestData, ResponseData, Collection, Environment, KeyValue, Method, Workspace } from "../types";
import { db } from "../lib/db";
import { resolveVariables } from "../lib/variables";
import { runScript } from "../lib/scriptEngine";
import { ScriptLog, TestResult } from "../types";

export const generateId = () => Math.random().toString(36).substring(2, 15);

interface Tab {
  id: string;
  request: RequestData;
  savedRequest: RequestData;
  response: ResponseData | null;
  isDirty: boolean;
  lastSavedAt: number | null;
}

interface AppState {
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  tabs: Tab[];
  activeTabId: string;
  isLoading: boolean;
  collections: Collection[];
  savedRequests: RequestData[];
  history: any[];
  environments: Environment[];
  activeEnvironmentId: string | null;
  sidebarTab: "collections" | "history" | "environments" | "workspaces";
  requestPanelTab: "params" | "auth" | "headers" | "body" | "scripts" | "tests";
  expandedCollections: Record<string, boolean>;
  isInitialized: boolean;

  // Actions
  init: () => Promise<void>;
  addWorkspace: (name: string) => Promise<string>;
  renameWorkspace: (id: string, name: string) => Promise<void>;
  deleteWorkspace: (id: string) => Promise<void>;
  setActiveWorkspace: (id: string) => Promise<void>;
  duplicateWorkspace: (id: string) => Promise<string>;
  addTab: (request?: RequestData) => void;
  removeTab: (id: string) => void;
  setActiveTabId: (id: string) => void;
  setActiveRequest: (request: Partial<RequestData>) => void;
  setSidebarTab: (tab: "collections" | "history" | "environments" | "workspaces") => void;
  setRequestPanelTab: (tab: "params" | "auth" | "headers" | "body") => void;
  toggleCollection: (id: string) => void;
  sendRequest: () => Promise<void>;
  saveToHistory: (request: RequestData, response: ResponseData) => void;
  addCollection: (name: string) => string;
  renameCollection: (id: string, name: string) => void;
  addRequestToCollection: (collectionId: string, request: RequestData) => void;
  addEnvironment: (name: string) => string;
  renameEnvironment: (id: string, name: string) => void;
  setActiveEnvironment: (id: string | null) => void;
  updateEnvironmentVariable: (envId: string, variable: KeyValue) => void;
  deleteEnvironmentVariable: (envId: string, varId: string) => void;
  updateSavedRequest: (id: string, request: Partial<RequestData>) => void;
  deleteSavedRequest: (id: string) => void;
  deleteCollection: (id: string) => void;
  deleteEnvironment: (id: string) => void;
  duplicateEnvironment: (id: string) => string;
  duplicateRequest: (id: string) => void;
  saveTab: (tabId: string) => Promise<void>;
  saveAllTabs: () => Promise<void>;
  discardChanges: (tabId: string) => void;
  discardAllChanges: () => void;
  updateGlobalVariable: (workspaceId: string, variable: KeyValue) => void;
  deleteGlobalVariable: (workspaceId: string, varId: string) => void;
  updateCollectionVariable: (collectionId: string, variable: KeyValue) => void;
  deleteCollectionVariable: (collectionId: string, varId: string) => void;
  importCollection: (data: any) => void;
  clearAllData: () => Promise<void>;
}

const createNewRequest = (workspaceId: string, name = "New Request"): RequestData => ({
  id: generateId(),
  workspaceId,
  name,
  method: "GET",
  url: "",
  headers: [{ id: generateId(), key: "", value: "", enabled: true }],
  params: [{ id: generateId(), key: "", value: "", enabled: true }],
  body: "",
  bodyType: "none",
  auth: { type: "none" },
  createdAt: Date.now(),
  preRequestScript: "",
  testScript: "",
});

const isRequestDirty = (current: RequestData, saved: RequestData) => {
  const fieldsToCompare: (keyof RequestData)[] = [
    "method", "url", "headers", "params", "body", "bodyType", "auth", "preRequestScript", "testScript"
  ];
  
  for (const field of fieldsToCompare) {
    if (JSON.stringify(current[field]) !== JSON.stringify(saved[field])) {
      return true;
    }
  }
  return false;
};

const initialTabId = generateId();

export const useStore = create<AppState>((set, get) => ({
  workspaces: [],
  activeWorkspaceId: null,
  tabs: [],
  activeTabId: "",
  isLoading: false,
  collections: [],
  savedRequests: [],
  history: [],
  environments: [],
  activeEnvironmentId: null,
  sidebarTab: "collections",
  requestPanelTab: "params",
  expandedCollections: {},
  isInitialized: false,

  init: async () => {
    let workspaces = await db.workspaces.toArray();
    let settings = await db.settings.get('main');
    
    // Create default workspace if none exists
    if (workspaces.length === 0) {
      const defaultWorkspace: Workspace = {
        id: generateId(),
        name: "My Workspace",
        color: "#FF6A1A",
        isActive: true,
        variables: [],
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      await db.workspaces.put(defaultWorkspace);
      workspaces = [defaultWorkspace];
    }

    // Ensure all workspaces have variables array
    workspaces = workspaces.map(w => ({ ...w, variables: w.variables || [] }));

    let activeWorkspaceId = settings?.activeWorkspaceId || workspaces.find(w => w.isActive)?.id || workspaces[0].id;
    
    // Ensure active workspace exists
    if (!workspaces.find(w => w.id === activeWorkspaceId)) {
      activeWorkspaceId = workspaces[0].id;
    }

    const [collections, savedRequests, history, environments, tabState] = await Promise.all([
      db.collections.where('workspaceId').equals(activeWorkspaceId).toArray(),
      db.requests.where('workspaceId').equals(activeWorkspaceId).toArray(),
      db.history.where('workspaceId').equals(activeWorkspaceId).toArray().then(arr => arr.sort((a, b) => b.timestamp - a.timestamp)),
      db.environments.where('workspaceId').equals(activeWorkspaceId).toArray(),
      db.tabState.get(activeWorkspaceId)
    ]);

    const initialTabId = generateId();
    const req = createNewRequest(activeWorkspaceId);
    const initialTab: Tab = {
      id: initialTabId,
      request: req,
      savedRequest: JSON.parse(JSON.stringify(req)),
      response: null,
      isDirty: false,
      lastSavedAt: Date.now(),
    };

    // Migrate old tabs if needed
    const migratedTabs = (tabState?.tabs || [initialTab]).map((t: any) => ({
      ...t,
      savedRequest: t.savedRequest || JSON.parse(JSON.stringify(t.request)),
      isDirty: t.isDirty ?? false,
      lastSavedAt: t.lastSavedAt ?? Date.now()
    }));

    set({
      workspaces,
      activeWorkspaceId,
      collections,
      savedRequests,
      history,
      environments,
      activeEnvironmentId: settings?.activeEnvironmentId || null,
      sidebarTab: (settings?.sidebarTab as any) || "collections",
      requestPanelTab: (settings?.requestPanelTab as any) || "params",
      tabs: migratedTabs,
      activeTabId: tabState?.activeTabId || initialTabId,
      isInitialized: true
    });
  },

  addWorkspace: async (name) => {
    const id = generateId();
    const workspace: Workspace = {
      id,
      name,
      color: "#FF6A1A",
      isActive: false,
      variables: [],
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    await db.workspaces.put(workspace);
    set(state => ({ workspaces: [...state.workspaces, workspace] }));
    return id;
  },

  renameWorkspace: async (id, name) => {
    await db.workspaces.update(id, { name, updatedAt: Date.now() });
    set(state => ({
      workspaces: state.workspaces.map(w => w.id === id ? { ...w, name, updatedAt: Date.now() } : w)
    }));
  },

  deleteWorkspace: async (id) => {
    const { workspaces, activeWorkspaceId } = get();
    if (workspaces.length <= 1) return;
    if (activeWorkspaceId === id) {
      const nextWorkspace = workspaces.find(w => w.id !== id);
      if (nextWorkspace) await get().setActiveWorkspace(nextWorkspace.id);
    }

    await Promise.all([
      db.workspaces.delete(id),
      db.collections.where('workspaceId').equals(id).delete(),
      db.requests.where('workspaceId').equals(id).delete(),
      db.history.where('workspaceId').equals(id).delete(),
      db.environments.where('workspaceId').equals(id).delete(),
      db.tabState.delete(id)
    ]);

    set(state => ({
      workspaces: state.workspaces.filter(w => w.id !== id)
    }));
  },

  setActiveWorkspace: async (id) => {
    const { activeWorkspaceId, sidebarTab, requestPanelTab, activeEnvironmentId } = get();
    if (activeWorkspaceId === id) return;

    // Update DB
    await Promise.all([
      db.workspaces.toCollection().modify({ isActive: false }),
      db.workspaces.update(id, { isActive: true }),
      db.settings.put({ 
        id: 'main', 
        theme: 'dark', 
        activeWorkspaceId: id,
        activeEnvironmentId,
        sidebarTab,
        requestPanelTab
      })
    ]);

    // Load new data
    const [collections, savedRequests, history, environments, tabState] = await Promise.all([
      db.collections.where('workspaceId').equals(id).toArray().then(arr => arr.map(c => ({ ...c, variables: c.variables || [] }))),
      db.requests.where('workspaceId').equals(id).toArray(),
      db.history.where('workspaceId').equals(id).toArray().then(arr => arr.sort((a, b) => b.timestamp - a.timestamp)),
      db.environments.where('workspaceId').equals(id).toArray(),
      db.tabState.get(id)
    ]);

    const initialTabId = generateId();
    const req = createNewRequest(id);
    const initialTab: Tab = {
      id: initialTabId,
      request: req,
      savedRequest: JSON.parse(JSON.stringify(req)),
      response: null,
      isDirty: false,
      lastSavedAt: Date.now(),
    };

    const migratedTabs = (tabState?.tabs || [initialTab]).map((t: any) => ({
      ...t,
      savedRequest: t.savedRequest || JSON.parse(JSON.stringify(t.request)),
      isDirty: t.isDirty ?? false,
      lastSavedAt: t.lastSavedAt ?? Date.now()
    }));

    set({
      activeWorkspaceId: id,
      collections,
      savedRequests,
      history,
      environments,
      activeEnvironmentId: null, // Reset env on switch or keep? Usually reset is safer
      tabs: migratedTabs,
      activeTabId: tabState?.activeTabId || initialTabId,
      workspaces: get().workspaces.map(w => ({ ...w, isActive: w.id === id }))
    });
  },

  duplicateWorkspace: async (id) => {
    const { workspaces } = get();
    const original = workspaces.find(w => w.id === id);
    if (!original) return "";

    const newId = generateId();
    const newWorkspace: Workspace = {
      ...original,
      id: newId,
      name: `${original.name} (Copy)`,
      isActive: false,
      variables: [...(original.variables || [])],
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    await db.workspaces.put(newWorkspace);

    // Copy collections and requests
    const collections = await db.collections.where('workspaceId').equals(id).toArray();
    for (const col of collections) {
      const newColId = generateId();
      const requests = await db.requests.where('collectionId').equals(col.id).toArray();
      const newRequestIds: string[] = [];

      for (const req of requests) {
        const newReqId = generateId();
        await db.requests.put({
          ...req,
          id: newReqId,
          workspaceId: newId,
          collectionId: newColId
        });
        newRequestIds.push(newReqId);
      }

      await db.collections.put({
        ...col,
        id: newColId,
        workspaceId: newId,
        requests: newRequestIds,
        variables: [...(col.variables || [])]
      });
    }

    // Copy environments
    const environments = await db.environments.where('workspaceId').equals(id).toArray();
    for (const env of environments) {
      await db.environments.put({
        ...env,
        id: generateId(),
        workspaceId: newId
      });
    }

    set(state => ({ workspaces: [...state.workspaces, newWorkspace] }));
    return newId;
  },

  addTab: (request) => {
    const { activeWorkspaceId } = get();
    if (!activeWorkspaceId) return;

    const id = generateId();
    const req = request || createNewRequest(activeWorkspaceId);
    const newTab: Tab = {
      id,
      request: req,
      savedRequest: JSON.parse(JSON.stringify(req)),
      response: null,
      isDirty: false,
      lastSavedAt: Date.now(),
    };
    set((state) => {
      const newState = {
        tabs: [...state.tabs, newTab],
        activeTabId: id,
      };
      db.tabState.put({ id: activeWorkspaceId, workspaceId: activeWorkspaceId, ...newState });
      return newState;
    });
  },

  removeTab: (id) => {
    const { activeWorkspaceId } = get();
    if (!activeWorkspaceId) return;

    set((state) => {
      let newState;
      if (state.tabs.length === 1) {
        const newId = generateId();
        const req = createNewRequest(activeWorkspaceId);
        newState = {
          tabs: [{ 
            id: newId, 
            request: req, 
            savedRequest: JSON.parse(JSON.stringify(req)),
            response: null,
            isDirty: false,
            lastSavedAt: Date.now()
          }],
          activeTabId: newId,
        };
      } else {
        const newTabs = state.tabs.filter((t) => t.id !== id);
        let newActiveTabId = state.activeTabId;
        if (state.activeTabId === id) {
          newActiveTabId = newTabs[newTabs.length - 1].id;
        }
        newState = { tabs: newTabs, activeTabId: newActiveTabId };
      }
      db.tabState.put({ id: activeWorkspaceId, workspaceId: activeWorkspaceId, ...newState });
      return newState;
    });
  },

  setActiveTabId: (id) => {
    const { activeWorkspaceId, tabs } = get();
    if (!activeWorkspaceId) return;

    set({ activeTabId: id });
    db.tabState.put({ id: activeWorkspaceId, workspaceId: activeWorkspaceId, tabs, activeTabId: id });
  },

  setActiveRequest: (request) => {
    const { activeWorkspaceId, activeTabId } = get();
    if (!activeWorkspaceId) return;

    set((state) => {
      const newTabs = state.tabs.map((t) => {
        if (t.id === activeTabId) {
          const updatedRequest = { ...t.request, ...request };
          return {
            ...t,
            request: updatedRequest,
            isDirty: isRequestDirty(updatedRequest, t.savedRequest)
          };
        }
        return t;
      });
      db.tabState.put({ id: activeWorkspaceId, workspaceId: activeWorkspaceId, tabs: newTabs, activeTabId });
      return { tabs: newTabs };
    });
  },

  setSidebarTab: (tab) => {
    set({ sidebarTab: tab });
    const { activeEnvironmentId, requestPanelTab, activeWorkspaceId } = get();
    db.settings.put({ id: 'main', theme: 'dark', activeEnvironmentId, sidebarTab: tab, requestPanelTab, activeWorkspaceId });
  },

  setRequestPanelTab: (tab) => {
    set({ requestPanelTab: tab });
    const { activeEnvironmentId, sidebarTab, activeWorkspaceId } = get();
    db.settings.put({ id: 'main', theme: 'dark', activeEnvironmentId, sidebarTab, requestPanelTab: tab, activeWorkspaceId });
  },

  toggleCollection: (id) =>
    set((state) => ({
      expandedCollections: {
        ...state.expandedCollections,
        [id]: !state.expandedCollections[id],
      },
    })),

  sendRequest: async () => {
    const { tabs, activeTabId, activeEnvironmentId, environments, activeWorkspaceId, collections, workspaces } = get();
    const activeTab = tabs.find((t) => t.id === activeTabId);
    if (!activeTab) return;

    set({ isLoading: true });

    const { request } = activeTab;
    const activeEnv = environments.find((e) => e.id === activeEnvironmentId);
    const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);
    const activeCollection = collections.find((c) => c.id === request.collectionId);

    // Build prioritized scopes
    const scopes: Array<{ name: string; variables: KeyValue[] }> = [];
    if (activeCollection) {
      scopes.push({ name: "Collection", variables: activeCollection.variables || [] });
    }
    if (activeEnv) {
      scopes.push({ name: "Environment", variables: activeEnv.variables || [] });
    }
    if (activeWorkspace) {
      scopes.push({ name: "Global", variables: activeWorkspace.variables || [] });
    }

    // Flatten for scripts
    const allVariables: KeyValue[] = [];
    [...scopes].reverse().forEach(s => {
      s.variables.forEach(v => {
        if (v.enabled && v.key) {
          const existing = allVariables.find(av => av.key === v.key);
          if (existing) {
            existing.value = v.value;
          } else {
            allVariables.push({ ...v });
          }
        }
      });
    });

    let currentRequest = { ...request };
    let scriptLogs: ScriptLog[] = [];
    let testResults: TestResult[] = [];

    // 1. Pre-request Script
    if (currentRequest.preRequestScript) {
      const result = await runScript(currentRequest.preRequestScript, {
        request: currentRequest,
        variables: allVariables,
        onVariableUpdate: (key, value) => {
          if (activeEnvironmentId) {
            const existing = allVariables.find(v => v.key === key);
            get().updateEnvironmentVariable(activeEnvironmentId, { 
              id: existing?.id || generateId(), 
              key, 
              value, 
              enabled: true 
            });
          }
        },
        onVariableUnset: (key) => {
          if (activeEnvironmentId) {
            const v = allVariables.find(v => v.key === key);
            if (v) get().deleteEnvironmentVariable(activeEnvironmentId, v.id);
          }
        }
      });
      currentRequest = result.request;
      scriptLogs.push(...result.logs);
    }

    // Replace variables using prioritized scopes
    let url = resolveVariables(currentRequest.url, scopes);
    let body = resolveVariables(currentRequest.body, scopes);

    // Prepare headers
    const headers: Record<string, string> = {};
    currentRequest.headers.forEach((h) => {
      if (h.enabled && h.key) {
        headers[resolveVariables(h.key, scopes)] = resolveVariables(h.value, scopes);
      }
    });

    // Prepare params
    let finalUrl = url;
    try {
      const urlObj = new URL(url.startsWith("http") ? url : `http://${url}`);
      currentRequest.params.forEach((p) => {
        if (p.enabled && p.key) {
          urlObj.searchParams.append(resolveVariables(p.key, scopes), resolveVariables(p.value, scopes));
        }
      });

      // Handle Authentication
      if (currentRequest.auth.type === "apiKey" && currentRequest.auth.apiKey) {
        const { key, value, addTo } = currentRequest.auth.apiKey;
        if (key && value) {
          if (addTo === "header") {
            headers[key] = value;
          } else {
            urlObj.searchParams.append(key, value);
          }
        }
      } else if (currentRequest.auth.type === "bearer" && currentRequest.auth.bearer?.token) {
        headers["Authorization"] = `Bearer ${currentRequest.auth.bearer.token}`;
      } else if (currentRequest.auth.type === "basic" && currentRequest.auth.basic) {
        const { username, password } = currentRequest.auth.basic;
        const credentials = btoa(`${username}:${password}`);
        headers["Authorization"] = `Basic ${credentials}`;
      }
      finalUrl = urlObj.toString();
    } catch (e) {
      // Invalid URL, keep as is
    }

    const startTime = Date.now();
    try {
      // Direct fetch (Local-first, no backend proxy)
      const response = await fetch(finalUrl, {
        method: currentRequest.method,
        headers: {
          ...headers,
          ...(currentRequest.bodyType === "json" ? { "Content-Type": "application/json" } : {}),
        },
        body: currentRequest.bodyType === "none" ? undefined : body,
      });

      const endTime = Date.now();
      const time = endTime - startTime;
      
      let responseData;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((v, k) => {
        responseHeaders[k] = v;
      });

      const size = new Blob([typeof responseData === 'string' ? responseData : JSON.stringify(responseData)]).size;

      let fullResponse: ResponseData = {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        data: responseData,
        time,
        size,
      };

      // 2. Test Script
      if (currentRequest.testScript) {
        const result = await runScript(currentRequest.testScript, {
          request: currentRequest,
          response: fullResponse,
          variables: allVariables,
          onVariableUpdate: (key, value) => {
            if (activeEnvironmentId) {
              const existing = allVariables.find(v => v.key === key);
              get().updateEnvironmentVariable(activeEnvironmentId, { 
                id: existing?.id || generateId(), 
                key, 
                value, 
                enabled: true 
              });
            }
          },
          onVariableUnset: (key) => {
            if (activeEnvironmentId) {
              const v = allVariables.find(v => v.key === key);
              if (v) get().deleteEnvironmentVariable(activeEnvironmentId, v.id);
            }
          }
        });
        testResults = result.testResults;
        scriptLogs.push(...result.logs);
      }

      fullResponse = {
        ...fullResponse,
        testResults,
        logs: scriptLogs
      };

      set((state) => {
        const newTabs = state.tabs.map((t) =>
          t.id === activeTabId ? { ...t, response: fullResponse } : t
        );
        if (state.activeWorkspaceId) {
          db.tabState.put({ id: state.activeWorkspaceId, workspaceId: state.activeWorkspaceId, tabs: newTabs, activeTabId: state.activeTabId });
        }
        return { tabs: newTabs, isLoading: false };
      });

      get().saveToHistory(currentRequest, fullResponse);
    } catch (error: any) {
      const endTime = Date.now();
      const fullResponse: ResponseData = {
        status: 0,
        statusText: "Error",
        headers: {},
        data: null,
        time: endTime - startTime,
        size: 0,
        error: error.message + ". Note: Browser CORS policy may block direct requests. Ensure the target server allows CORS.",
        logs: scriptLogs,
        testResults
      };

      set((state) => {
        const newTabs = state.tabs.map((t) =>
          t.id === activeTabId ? { ...t, response: fullResponse } : t
        );
        if (state.activeWorkspaceId) {
          db.tabState.put({ id: state.activeWorkspaceId, workspaceId: state.activeWorkspaceId, tabs: newTabs, activeTabId: state.activeTabId });
        }
        return { tabs: newTabs, isLoading: false };
      });
    }
  },

  saveToHistory: (request, response) => {
    const { activeWorkspaceId } = get();
    if (!activeWorkspaceId) return;

    const historyItem = {
      id: generateId(),
      workspaceId: activeWorkspaceId,
      request,
      response,
      timestamp: Date.now(),
    };
    set((state) => {
      const newHistory = [historyItem, ...state.history].slice(0, 100);
      db.history.put(historyItem);
      return { history: newHistory };
    });
  },

  addCollection: (name) => {
    const { activeWorkspaceId } = get();
    if (!activeWorkspaceId) return "";

    const id = generateId();
    const collection: Collection = { id, workspaceId: activeWorkspaceId, name, requests: [], variables: [], createdAt: Date.now() };
    set((state) => ({
      collections: [...state.collections, collection],
    }));
    db.collections.put(collection);
    return id;
  },

  renameCollection: (id, name) => {
    set((state) => {
      const newCollections = state.collections.map((c) =>
        c.id === id ? { ...c, name } : c
      );
      const collection = newCollections.find(c => c.id === id);
      if (collection) db.collections.put(collection);
      return { collections: newCollections };
    });
  },

  addRequestToCollection: (collectionId, request) => {
    const { activeWorkspaceId } = get();
    if (!activeWorkspaceId) return;

    const requestId = request.id || generateId();
    const newRequest = { ...request, id: requestId, workspaceId: activeWorkspaceId, collectionId };
    
    set((state) => {
      const newSavedRequests = [...state.savedRequests, newRequest];
      const newCollections = state.collections.map((c) =>
        c.id === collectionId ? { ...c, requests: [...c.requests, requestId] } : c
      );
      
      db.requests.put(newRequest);
      const collection = newCollections.find(c => c.id === collectionId);
      if (collection) db.collections.put(collection);
      
      return {
        savedRequests: newSavedRequests,
        collections: newCollections,
      };
    });
  },

  addEnvironment: (name) => {
    const { activeWorkspaceId } = get();
    if (!activeWorkspaceId) return "";

    const id = generateId();
    const now = Date.now();
    const env: Environment = { id, workspaceId: activeWorkspaceId, name, variables: [], createdAt: now, updatedAt: now };
    set((state) => ({
      environments: [...state.environments, env],
    }));
    db.environments.put(env);
    return id;
  },

  renameEnvironment: (id, name) => {
    set((state) => {
      const now = Date.now();
      const newEnvironments = state.environments.map((e) =>
        e.id === id ? { ...e, name, updatedAt: now } : e
      );
      const env = newEnvironments.find(e => e.id === id);
      if (env) db.environments.put(env);
      return { environments: newEnvironments };
    });
  },

  setActiveEnvironment: (id) => {
    set({ activeEnvironmentId: id });
    const { sidebarTab, requestPanelTab, activeWorkspaceId } = get();
    db.settings.put({ id: 'main', theme: 'dark', activeEnvironmentId: id, sidebarTab, requestPanelTab, activeWorkspaceId });
  },

  updateEnvironmentVariable: (envId, variable) => {
    set((state) => {
      const now = Date.now();
      const newEnvironments = state.environments.map((e) =>
        e.id === envId
          ? {
              ...e,
              updatedAt: now,
              variables: e.variables.find((v) => v.id === variable.id)
                ? e.variables.map((v) => (v.id === variable.id ? variable : v))
                : [...e.variables, variable],
            }
          : e
      );
      const env = newEnvironments.find(e => e.id === envId);
      if (env) db.environments.put(env);
      return { environments: newEnvironments };
    });
  },

  deleteEnvironmentVariable: (envId, varId) => {
    set((state) => {
      const now = Date.now();
      const newEnvironments = state.environments.map((e) =>
        e.id === envId
          ? { ...e, updatedAt: now, variables: e.variables.filter((v) => v.id !== varId) }
          : e
      );
      const env = newEnvironments.find(e => e.id === envId);
      if (env) db.environments.put(env);
      return { environments: newEnvironments };
    });
  },

  updateSavedRequest: (id, request) => {
    set((state) => {
      const oldRequest = state.savedRequests.find((r) => r.id === id);
      const newCollectionId = request.collectionId;
      
      let newCollections = state.collections;
      if (oldRequest && newCollectionId && oldRequest.collectionId !== newCollectionId) {
        // Remove from old collection
        newCollections = newCollections.map((c) =>
          c.id === oldRequest.collectionId
            ? { ...c, requests: c.requests.filter((rid) => rid !== id) }
            : c
        );
        // Add to new collection
        newCollections = newCollections.map((c) =>
          c.id === newCollectionId
            ? { ...c, requests: [...c.requests, id] }
            : c
        );
        
        const oldCol = newCollections.find(c => c.id === oldRequest.collectionId);
        const newCol = newCollections.find(c => c.id === newCollectionId);
        if (oldCol) db.collections.put(oldCol);
        if (newCol) db.collections.put(newCol);
      }

      const newSavedRequests = state.savedRequests.map((r) =>
        r.id === id ? { ...r, ...request } : r
      );
      const updatedReq = newSavedRequests.find(r => r.id === id);
      if (updatedReq) db.requests.put(updatedReq);

      return {
        savedRequests: newSavedRequests,
        collections: newCollections,
      };
    });
  },

  deleteSavedRequest: (id) => {
    set((state) => {
      const requestToDelete = state.savedRequests.find(r => r.id === id);
      const newCollections = state.collections.map((c) => {
        if (requestToDelete && c.id === requestToDelete.collectionId) {
          const newC = { ...c, requests: c.requests.filter((rid) => rid !== id) };
          db.collections.put(newC);
          return newC;
        }
        return c;
      });
      
      db.requests.delete(id);
      
      return {
        savedRequests: state.savedRequests.filter((r) => r.id !== id),
        collections: newCollections,
      };
    });
  },

  deleteCollection: (id) => {
    set((state) => {
      const requestsToDelete = state.savedRequests.filter(r => r.collectionId === id);
      requestsToDelete.forEach(r => db.requests.delete(r.id));
      db.collections.delete(id);
      
      return {
        collections: state.collections.filter((c) => c.id !== id),
        savedRequests: state.savedRequests.filter((r) => r.collectionId !== id),
      };
    });
  },

  deleteEnvironment: (id) => {
    set((state) => {
      db.environments.delete(id);
      return {
        environments: state.environments.filter((e) => e.id !== id),
        activeEnvironmentId: state.activeEnvironmentId === id ? null : state.activeEnvironmentId,
      };
    });
  },

  duplicateEnvironment: (id) => {
    const { activeWorkspaceId } = get();
    if (!activeWorkspaceId) return "";

    const newId = generateId();
    set((state) => {
      const original = state.environments.find((e) => e.id === id);
      if (!original) return state;

      const now = Date.now();
      const newEnv: Environment = {
        ...original,
        id: newId,
        workspaceId: activeWorkspaceId,
        name: `${original.name} (Copy)`,
        createdAt: now,
        updatedAt: now,
      };

      db.environments.put(newEnv);
      return {
        environments: [...state.environments, newEnv],
      };
    });
    return newId;
  },

  duplicateRequest: (id) => {
    const { activeWorkspaceId } = get();
    if (!activeWorkspaceId) return;

    set((state) => {
      const original = state.savedRequests.find((r) => r.id === id);
      if (!original) return state;

      const newId = generateId();
      const duplicated = {
        ...original,
        id: newId,
        workspaceId: activeWorkspaceId,
        name: `${original.name} (Copy)`,
        createdAt: Date.now(),
      };

      db.requests.put(duplicated);
      const collection = state.collections.find(c => c.id === original.collectionId);
      if (collection) {
        const newC = { ...collection, requests: [...collection.requests, newId] };
        db.collections.put(newC);
        return {
          savedRequests: [...state.savedRequests, duplicated],
          collections: state.collections.map((c) => c.id === original.collectionId ? newC : c),
        };
      }

      return {
        savedRequests: [...state.savedRequests, duplicated],
      };
    });
  },

  saveTab: async (tabId: string) => {
    const { tabs, activeWorkspaceId, activeTabId } = get();
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    const requestToSave = { ...tab.request };
    
    // If it's a saved request (part of a collection), update it in DB
    if (requestToSave.collectionId) {
      await db.requests.put(requestToSave);
      set(state => ({
        savedRequests: state.savedRequests.map(r => r.id === requestToSave.id ? requestToSave : r)
      }));
    }

    set(state => {
      const newTabs = state.tabs.map(t => t.id === tabId ? {
        ...t,
        savedRequest: JSON.parse(JSON.stringify(requestToSave)),
        isDirty: false,
        lastSavedAt: Date.now()
      } : t);
      
      if (activeWorkspaceId) {
        db.tabState.put({ id: activeWorkspaceId, workspaceId: activeWorkspaceId, tabs: newTabs, activeTabId });
      }
      return { tabs: newTabs };
    });
  },

  saveAllTabs: async () => {
    const { tabs, activeWorkspaceId, activeTabId } = get();
    const dirtyTabs = tabs.filter(t => t.isDirty);
    if (dirtyTabs.length === 0) return;

    const updatedSavedRequests = [...get().savedRequests];
    const newTabs = tabs.map(t => {
      if (t.isDirty) {
        const requestToSave = { ...t.request };
        if (requestToSave.collectionId) {
          db.requests.put(requestToSave);
          const idx = updatedSavedRequests.findIndex(r => r.id === requestToSave.id);
          if (idx !== -1) updatedSavedRequests[idx] = requestToSave;
        }
        return {
          ...t,
          savedRequest: JSON.parse(JSON.stringify(requestToSave)),
          isDirty: false,
          lastSavedAt: Date.now()
        };
      }
      return t;
    });

    set({ tabs: newTabs, savedRequests: updatedSavedRequests });
    if (activeWorkspaceId) {
      db.tabState.put({ id: activeWorkspaceId, workspaceId: activeWorkspaceId, tabs: newTabs, activeTabId });
    }
  },

  discardChanges: (tabId: string) => {
    const { activeWorkspaceId, activeTabId } = get();
    set(state => {
      const newTabs = state.tabs.map(t => t.id === tabId ? {
        ...t,
        request: JSON.parse(JSON.stringify(t.savedRequest)),
        isDirty: false
      } : t);
      
      if (activeWorkspaceId) {
        db.tabState.put({ id: activeWorkspaceId, workspaceId: activeWorkspaceId, tabs: newTabs, activeTabId });
      }
      return { tabs: newTabs };
    });
  },

  discardAllChanges: () => {
    const { activeWorkspaceId, activeTabId, tabs } = get();
    const newTabs = tabs.map(t => ({
      ...t,
      request: JSON.parse(JSON.stringify(t.savedRequest)),
      isDirty: false
    }));
    
    set({ tabs: newTabs });
    if (activeWorkspaceId) {
      db.tabState.put({ id: activeWorkspaceId, workspaceId: activeWorkspaceId, tabs: newTabs, activeTabId });
    }
  },

  updateGlobalVariable: (workspaceId, variable) => {
    set((state) => {
      const newWorkspaces = state.workspaces.map((w) => {
        if (w.id === workspaceId) {
          const variables = w.variables || [];
          const exists = variables.find((v) => v.id === variable.id);
          const newVariables = exists
            ? variables.map((v) => (v.id === variable.id ? variable : v))
            : [...variables, variable];
          return { ...w, variables: newVariables, updatedAt: Date.now() };
        }
        return w;
      });
      const updated = newWorkspaces.find(w => w.id === workspaceId);
      if (updated) db.workspaces.put(updated);
      return { workspaces: newWorkspaces };
    });
  },

  deleteGlobalVariable: (workspaceId, varId) => {
    set((state) => {
      const newWorkspaces = state.workspaces.map((w) => {
        if (w.id === workspaceId) {
          return { ...w, variables: (w.variables || []).filter((v) => v.id !== varId), updatedAt: Date.now() };
        }
        return w;
      });
      const updated = newWorkspaces.find(w => w.id === workspaceId);
      if (updated) db.workspaces.put(updated);
      return { workspaces: newWorkspaces };
    });
  },

  updateCollectionVariable: (collectionId, variable) => {
    set((state) => {
      const newCollections = state.collections.map((c) => {
        if (c.id === collectionId) {
          const variables = c.variables || [];
          const exists = variables.find((v) => v.id === variable.id);
          const newVariables = exists
            ? variables.map((v) => (v.id === variable.id ? variable : v))
            : [...variables, variable];
          return { ...c, variables: newVariables };
        }
        return c;
      });
      const updated = newCollections.find(c => c.id === collectionId);
      if (updated) db.collections.put(updated);
      return { collections: newCollections };
    });
  },

  deleteCollectionVariable: (collectionId, varId) => {
    set((state) => {
      const newCollections = state.collections.map((c) => {
        if (c.id === collectionId) {
          return { ...c, variables: (c.variables || []).filter((v) => v.id !== varId) };
        }
        return c;
      });
      const updated = newCollections.find(c => c.id === collectionId);
      if (updated) db.collections.put(updated);
      return { collections: newCollections };
    });
  },

  importCollection: (data) => {
    const { activeWorkspaceId } = get();
    if (!activeWorkspaceId) return;

    let newCollection: Collection;
    let newRequests: RequestData[] = [];
    const newCollectionId = generateId();

    // Check if it's a Postman Collection (v2.1)
    if (data.info && data.item) {
      newCollection = {
        id: newCollectionId,
        workspaceId: activeWorkspaceId,
        name: data.info.name || "Imported Postman Collection",
        requests: [],
        variables: [],
        createdAt: Date.now(),
      };

      const parseItems = (items: any[]) => {
        items.forEach((item: any) => {
          if (item.request) {
            // It's a request
            const requestId = generateId();
            const req: RequestData = {
              id: requestId,
              workspaceId: activeWorkspaceId,
              collectionId: newCollectionId,
              name: item.name || "Untitled Request",
              method: (item.request.method || "GET") as Method,
              url: typeof item.request.url === "string" ? item.request.url : (item.request.url?.raw || ""),
              headers: (item.request.header || []).map((h: any) => ({
                id: generateId(),
                key: h.key || "",
                value: h.value || "",
                enabled: !h.disabled,
              })),
              params: (item.request.url?.query || []).map((q: any) => ({
                id: generateId(),
                key: q.key || "",
                value: q.value || "",
                enabled: !q.disabled,
              })),
              body: item.request.body?.raw || "",
              bodyType: item.request.body?.mode === "raw" ? "json" : "none",
              auth: { type: "none" }, // Simplified auth import
              createdAt: Date.now(),
              preRequestScript: item.event?.find((e: any) => e.listen === "prerequest")?.script?.exec?.join("\n") || "",
              testScript: item.event?.find((e: any) => e.listen === "test")?.script?.exec?.join("\n") || "",
            };
            
            // Ensure arrays exist
            if (!req.headers) req.headers = [];
            if (!req.params) req.params = [];
            
            newRequests.push(req);
            newCollection.requests.push(requestId);
          } else if (item.item) {
            // It's a folder, recurse
            parseItems(item.item);
          }
        });
      };

      parseItems(data.item);
    } else {
      // Internal format (from export)
      const { requestsData, ...collectionData } = data;
      
      newRequests = (requestsData || []).map((r: any) => {
        const newId = generateId();
        return {
          ...r,
          id: newId,
          workspaceId: activeWorkspaceId,
          collectionId: newCollectionId,
          name: r.name || "Untitled Request",
          method: r.method || "GET",
          url: r.url || "",
          headers: r.headers || [],
          params: r.params || [],
          body: r.body || "",
          bodyType: r.bodyType || "none",
          auth: r.auth || { type: "none" },
          createdAt: Date.now(),
          preRequestScript: r.preRequestScript || "",
          testScript: r.testScript || "",
        };
      });

      newCollection = {
        ...collectionData,
        id: newCollectionId,
        workspaceId: activeWorkspaceId,
        name: collectionData.name || "Imported Collection",
        requests: newRequests.map((r: any) => r.id),
        createdAt: Date.now(),
      };
    }

    set((state) => ({
      collections: [...state.collections, newCollection],
      savedRequests: [...state.savedRequests, ...newRequests],
    }));

    db.collections.put(newCollection);
    newRequests.forEach((r: any) => db.requests.put(r));
  },

  clearAllData: async () => {
    await Promise.all([
      db.workspaces.clear(),
      db.collections.clear(),
      db.requests.clear(),
      db.history.clear(),
      db.environments.clear(),
      db.settings.clear(),
      db.tabState.clear()
    ]);
    window.location.reload();
  }
}));
